import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../../contexts/AuthContext";
import ShiftLogForm from "../Manufacturing/ShiftLogForm";

type MOStatus = "confirmed" | "in_progress" | "done" | "cancelled";

type MO = {
  id: number;
  name: string;
  product: string;
  planned_qty: number;
  status: MOStatus;
  scheduled_date: string | null;
  total_a_rolls: number;
  total_b_rolls: number;
  log_count: number;
};

type ShiftLog = {
  id: number;
  shift_id: number | null;
  shift_name: string | null;
  brand_id: number | null;
  brand_name: string | null;
  a_roll_count: number;
  b_roll_count: number;
  submitted_by: string;
  submitted_at: string;
  notes: string | null;
};

const STATUS_COLORS: Record<MOStatus, "default" | "warning" | "info" | "success" | "error"> = {
  confirmed: "warning",
  in_progress: "info",
  done: "success",
  cancelled: "error",
};

const STATUS_LABEL: Record<MOStatus, string> = {
  confirmed: "Confirmed",
  in_progress: "In Progress",
  done: "Done",
  cancelled: "Cancelled",
};

const Lam1OperatorPage: React.FC = () => {
  const { token } = useAuth();
  const api = process.env.REACT_APP_API_URL || "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [mos, setMos] = useState<MO[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<MO | null>(null);
  const [logs, setLogs] = useState<ShiftLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [tab, setTab] = useState(0);

  // Edit log dialog
  const [editLog, setEditLog] = useState<ShiftLog | null>(null);
  const [shifts, setShifts] = useState<{ id: number; name: string }[]>([]);
  const [brands, setBrands] = useState<{ id: number; name: string }[]>([]);
  const [eShiftId, setEShiftId] = useState<number | "">("");
  const [eBrandId, setEBrandId] = useState<number | "">("");
  const [eARolls, setEARolls] = useState("");
  const [eBRolls, setEBRolls] = useState("");
  const [eNotes, setENotes] = useState("");
  const [eSaving, setESaving] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: "success" | "error" }>
    ({ open: false, msg: "", severity: "success" });

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  const loadMos = useCallback(async (): Promise<MO[]> => {
    setLoading(true);
    try {
      const res = await fetch(`${api}/mos?status=confirmed,in_progress`, { headers });
      if (!res.ok) throw new Error();
      const data: MO[] = await res.json();
      setMos(data);
      return data;
    } catch {
      showSnack("Failed to load orders", "error");
      return [];
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { loadMos(); }, [loadMos]);

  const loadLogs = useCallback(async (moId: number) => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${api}/mos/${moId}`, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLogs(data.logs ?? []);
    } catch {
      showSnack("Failed to load log entries", "error");
    } finally {
      setLogsLoading(false);
    }
  }, []); // eslint-disable-line

  const loadMeta = useCallback(async () => {
    const [sRes, bRes] = await Promise.all([
      fetch(`${api}/settings/shifts`, { headers }),
      fetch(`${api}/brands`, { headers }),
    ]);
    if (sRes.ok) { const d = await sRes.json(); setShifts(d.shifts ?? []); }
    if (bRes.ok) { const d = await bRes.json(); setBrands(d.filter((b: any) => b.is_active)); }
  }, []); // eslint-disable-line

  const openMO = async (mo: MO) => {
    setSelected(mo);
    setTab(0);
    await Promise.all([loadLogs(mo.id), loadMeta()]);
  };

  const handleLogSubmitted = async () => {
    if (!selected) return;
    const [freshMos] = await Promise.all([loadMos(), loadLogs(selected.id)]);
    const updated = freshMos.find(m => m.id === selected.id);
    if (updated) setSelected(updated);
  };

  const openEditLog = (log: ShiftLog) => {
    setEditLog(log);
    setEShiftId(log.shift_id ?? "");
    setEBrandId(log.brand_id ?? "");
    setEARolls(String(log.a_roll_count));
    setEBRolls(String(log.b_roll_count));
    setENotes(log.notes ?? "");
  };

  const handleEditSave = async () => {
    if (!editLog || !selected) return;
    setESaving(true);
    try {
      const selectedShift = shifts.find(s => s.id === eShiftId);
      const selectedBrand = brands.find(b => b.id === eBrandId);
      const res = await fetch(`${api}/mos/${selected.id}/logs/${editLog.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          shift_id: eShiftId || null,
          shift_name: selectedShift?.name ?? null,
          brand_id: eBrandId || null,
          brand_name: selectedBrand?.name ?? null,
          a_roll_count: parseInt(eARolls) || 0,
          b_roll_count: parseInt(eBRolls) || 0,
          notes: eNotes.trim() || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Save failed");
      setEditLog(null);
      showSnack("Entry updated", "success");
      const [freshMos] = await Promise.all([loadMos(), loadLogs(selected.id)]);
      const updated = freshMos.find(m => m.id === selected.id);
      if (updated) setSelected(updated);
    } catch (e: any) {
      showSnack(e.message || "Save failed", "error");
    } finally {
      setESaving(false);
    }
  };

  const filtered = mos.filter(
    m =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.product.toLowerCase().includes(search.toLowerCase())
  );

  // ---- Detail view ----
  if (selected) {
    const produced = selected.total_a_rolls + selected.total_b_rolls;
    const pct = Math.min(Math.round((produced / selected.planned_qty) * 100), 100);

    return (
      <Box sx={{ p: { xs: 0, sm: 1 } }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mb={{ xs: 2, sm: 3 }}
          flexWrap="wrap"
        >
          <IconButton onClick={() => { setSelected(null); setLogs([]); }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ minWidth: 0, flex: 1, wordBreak: "break-word" }}
          >
            {selected.name}
          </Typography>
          <Chip label={STATUS_LABEL[selected.status]} color={STATUS_COLORS[selected.status]} size="small" />
        </Box>

        {/* Progress summary */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Box display="flex" gap={{ xs: 2, sm: 4 }} mb={1} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Product</Typography>
              <Typography fontWeight={500}>{selected.product}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Planned</Typography>
              <Typography fontWeight={500}>{selected.planned_qty}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Produced</Typography>
              <Typography fontWeight={500} color={produced > selected.planned_qty ? "error.main" : "inherit"}>
                {produced}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
              <Typography fontWeight={500}>{Math.max(selected.planned_qty - produced, 0)}</Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={produced > selected.planned_qty ? "error" : pct === 100 ? "success" : "primary"}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Typography variant="caption" color="text.secondary">{pct}%</Typography>
        </Paper>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
        >
          <Tab label={`Log History (${logs.length})`} />
          <Tab label="Submit Entry" disabled={selected.status === "done" || selected.status === "cancelled"} />
        </Tabs>

        {/* Log history */}
        {tab === 0 && (
          logsLoading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
          ) : logs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No entries yet. Use "Submit Entry" to add one.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Shift</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell align="right">A Rolls</TableCell>
                    <TableCell align="right">B Rolls</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map(l => (
                    <TableRow key={l.id}>
                      <TableCell>{new Date(l.submitted_at).toLocaleString()}</TableCell>
                      <TableCell>{l.shift_name ?? "—"}</TableCell>
                      <TableCell>{l.brand_name ?? "—"}</TableCell>
                      <TableCell align="right">{l.a_roll_count}</TableCell>
                      <TableCell align="right">{l.b_roll_count}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>{l.notes ?? "—"}</TableCell>
                      <TableCell>{l.submitted_by}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit entry">
                          <IconButton size="small" onClick={() => openEditLog(l)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {/* Submit entry */}
        {tab === 1 && (
          <ShiftLogForm
            api={api}
            token={token ?? undefined}
            moId={selected.id}
            moName={selected.name}
            onSubmitted={handleLogSubmitted}
          />
        )}

        {/* Edit log dialog */}
        <Dialog open={!!editLog} onClose={() => setEditLog(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit Log Entry</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
            <TextField select label="Shift" fullWidth value={eShiftId} onChange={e => setEShiftId(Number(e.target.value))}>
              <MenuItem value="">— None —</MenuItem>
              {shifts.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField select label="Brand" fullWidth value={eBrandId} onChange={e => setEBrandId(Number(e.target.value))}>
              <MenuItem value="">— None —</MenuItem>
              {brands.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>
            <Box display="flex" gap={2}>
              <TextField label="A Rolls" type="number" fullWidth value={eARolls} onChange={e => setEARolls(e.target.value)} slotProps={{ htmlInput: { min: 0 } }} />
              <TextField label="B Rolls" type="number" fullWidth value={eBRolls} onChange={e => setEBRolls(e.target.value)} slotProps={{ htmlInput: { min: 0 } }} />
            </Box>
            <TextField label="Notes" fullWidth multiline rows={2} value={eNotes} onChange={e => setENotes(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditLog(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleEditSave}
              disabled={eSaving || (parseInt(eARolls) === 0 && parseInt(eBRolls) === 0)}
            >
              {eSaving ? <CircularProgress size={18} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ---- MO list ----
  return (
    <Box sx={{ p: { xs: 0, sm: 1 } }}>
      <Typography
        variant="h5"
        fontWeight={700}
        mb={{ xs: 2, sm: 3 }}
      >
        Active Orders
      </Typography>

      <TextField
        size="small"
        placeholder="Search by MO # or product…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: { xs: "100%", sm: 320 } }}
        slotProps={{ input: { startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} /> } }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary">
          {mos.length === 0 ? "No active orders at this time." : "No orders match your search."}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>MO #</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Planned</TableCell>
                <TableCell align="right">Produced</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(mo => {
                const prod = mo.total_a_rolls + mo.total_b_rolls;
                const p = Math.min(Math.round((prod / mo.planned_qty) * 100), 100);
                return (
                  <TableRow key={mo.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{mo.name}</TableCell>
                    <TableCell>{mo.product}</TableCell>
                    <TableCell align="right">{mo.planned_qty}</TableCell>
                    <TableCell align="right">{prod}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={p}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                          color={prod > mo.planned_qty ? "error" : p === 100 ? "success" : "primary"}
                        />
                        <Typography variant="caption">{p}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABEL[mo.status]} color={STATUS_COLORS[mo.status]} size="small" />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => openMO(mo)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Lam1OperatorPage;
